import apiClient from './ApiClient';
import SessionManager from '../utils/SessionManager';
import { API_CONFIG } from '../config/api';

/**
 * Client Authentication Service - Manages client authentication and session state
 * Handles client signup, login, logout, token management, and session validation
 */
class ClientAuthService {
  constructor() {
    this.isLoggingIn = false;
    this.isSigningUp = false;
    this.currentClient = null;
    this.tokenStorageKey = 'seemanchal_client_token';
    this.userStorageKey = 'seemanchal_client_user';
  }

  /**
   * Get authentication token for API requests
   */
  getAuthToken() {
    return SessionManager.getToken(this.tokenStorageKey);
  }

  /**
   * Client signup with name, phone, address, and password
   * API expects: {"name": "string", "phone": "string", "address": "string", "password": "string"}
   * API returns: {"id": 0, "name": "string", "phone": "string", "address": "string", "created_at": "2025-12-27T13:39:05.105Z"}
   */
  async signup(clientData) {
    if (this.isSigningUp) {
      throw new Error('Signup already in progress');
    }

    if (!clientData || !clientData.name || !clientData.phone || !clientData.address || !clientData.password) {
      throw new Error('Name, phone, address, and password are required');
    }

    this.isSigningUp = true;

    try {
      // Make signup request to API
      const response = await apiClient.post(API_CONFIG.ENDPOINTS.CLIENT_SIGNUP, {
        name: clientData.name.trim(),
        phone: clientData.phone.trim(),
        address: clientData.address.trim(),
        password: clientData.password
      }, { skipAuth: true }); // Skip auth for signup request

      // Validate response format
      if (!response.id) {
        throw new Error('Invalid response format: missing client id');
      }

      // Store client info (but not token yet - they need to login)
      const clientInfo = {
        id: response.id,
        name: response.name,
        phone: response.phone,
        address: response.address,
        created_at: response.created_at
      };

      return {
        success: true,
        client: clientInfo,
        message: 'Signup successful! Please login to continue.'
      };

    } catch (error) {
      // Handle specific signup errors
      if (error.status === 422) {
        // Validation error
        if (error.data && error.data.detail) {
          const validationErrors = error.data.detail.map(err => err.msg).join(', ');
          throw new Error(validationErrors || 'Validation error');
        }
        throw new Error('Phone number may already exist or validation failed');
      } else if (error.status === 400) {
        throw new Error('Phone number already exists');
      } else if (error.name === 'NetworkError') {
        throw new Error('Unable to connect to server. Please check your internet connection.');
      } else if (error.name === 'TimeoutError') {
        throw new Error('Signup request timed out. Please try again.');
      }
      
      // Re-throw with original message or generic error
      throw new Error(error.message || 'Signup failed. Please try again.');
    } finally {
      this.isSigningUp = false;
    }
  }

  /**
   * Client login with phone and password
   * API expects: {"phone": "string", "password": "string"}
   * API returns: {"access_token": "string", "token_type": "string"}
   */
  async login(credentials) {
    if (this.isLoggingIn) {
      throw new Error('Login already in progress');
    }

    if (!credentials || !credentials.phone || !credentials.password) {
      throw new Error('Phone and password are required');
    }

    this.isLoggingIn = true;

    try {
      // Make login request to API
      const response = await apiClient.post(API_CONFIG.ENDPOINTS.CLIENT_LOGIN, {
        phone: credentials.phone.trim(),
        password: credentials.password
      }, { skipAuth: true }); // Skip auth for login request

      // Validate response format
      if (!response.access_token) {
        throw new Error('Invalid response format: missing access_token');
      }

      // Store token and client info
      const success = SessionManager.setToken(response.access_token, this.tokenStorageKey);
      if (!success) {
        throw new Error('Failed to store authentication token');
      }

      // Create client info from response
      const clientInfo = {
        phone: credentials.phone,
        token_type: response.token_type || 'bearer',
        loginTime: new Date().toISOString()
      };

      SessionManager.setUser(clientInfo, this.userStorageKey);
      this.currentClient = clientInfo;

      return {
        success: true,
        client: clientInfo,
        token: response.access_token,
        message: 'Login successful'
      };

    } catch (error) {
      // Handle specific login errors
      if (error.status === 401) {
        throw new Error('Invalid phone number or password');
      } else if (error.status === 422) {
        throw new Error('Invalid credentials format');
      } else if (error.status === 429) {
        throw new Error('Too many login attempts. Please try again later.');
      } else if (error.name === 'NetworkError') {
        throw new Error('Unable to connect to server. Please check your internet connection.');
      } else if (error.name === 'TimeoutError') {
        throw new Error('Login request timed out. Please try again.');
      }
      
      // Re-throw with original message or generic error
      throw new Error(error.message || 'Login failed. Please try again.');
    } finally {
      this.isLoggingIn = false;
    }
  }

  /**
   * Logout and clear all session data
   */
  logout() {
    try {
      // Clear all session data
      SessionManager.clearSession(this.tokenStorageKey, this.userStorageKey);
      this.currentClient = null;
      
      return {
        success: true,
        message: 'Logged out successfully'
      };
    } catch (error) {
      console.error('Logout error:', error);
      // Even if there's an error, try to clear what we can
      SessionManager.clearSession(this.tokenStorageKey, this.userStorageKey);
      this.currentClient = null;
      
      return {
        success: false,
        message: 'Logout completed with errors'
      };
    }
  }

  /**
   * Check if client is currently authenticated
   */
  isAuthenticated() {
    return SessionManager.isAuthenticated(this.tokenStorageKey);
  }

  /**
   * Get current authentication token
   */
  getToken() {
    return SessionManager.getToken(this.tokenStorageKey);
  }

  /**
   * Get current client information
   */
  getCurrentClient() {
    if (this.currentClient) {
      return this.currentClient;
    }
    
    // Try to load from storage
    this.currentClient = SessionManager.getUser(this.userStorageKey);
    return this.currentClient;
  }

  /**
   * Validate current session with server
   */
  async validateSession() {
    if (!this.isAuthenticated()) {
      return false;
    }

    try {
      const token = this.getToken();
      if (!token) {
        this.logout();
        return false;
      }

      // Check if token is expiring soon
      if (SessionManager.isTokenExpiringSoon(5, this.tokenStorageKey)) {
        console.warn('Token expiring soon');
      }

      return true;
    } catch (error) {
      console.error('Session validation error:', error);
      this.logout();
      return false;
    }
  }

  /**
   * Initialize authentication state on app startup
   */
  async initialize() {
    try {
      // Load client from storage
      this.currentClient = SessionManager.getUser(this.userStorageKey);
      
      // Validate session if client exists
      if (this.currentClient) {
        const isValid = await this.validateSession();
        if (!isValid) {
          this.currentClient = null;
        }
      }
      
      return this.isAuthenticated();
    } catch (error) {
      console.error('Client auth initialization error:', error);
      this.logout();
      return false;
    }
  }

  /**
   * Check if login is currently in progress
   */
  isLoginInProgress() {
    return this.isLoggingIn;
  }

  /**
   * Check if signup is currently in progress
   */
  isSignupInProgress() {
    return this.isSigningUp;
  }
}

// Export singleton instance
const clientAuthService = new ClientAuthService();
export default clientAuthService;

